import { isString } from "lodash";
import {
  OakFlex,
  OakIcon,
  OakQuizResultItem,
  OakSpan,
} from "@oaknational/oak-components";

import { QuestionState } from "@/components/PupilComponents/QuizUtils/questionTypes";
import { QuizResultQuestionStem } from "@/components/PupilComponents/QuizQuestions/QuizResultQuestionStem";
import { QuizResultMCQ } from "@/components/PupilComponents/QuizQuestions/QuizResultMCQ";
import { QuizResultShortAnswer } from "@/components/PupilComponents/QuizQuestions/QuizResultShortAnswer";
import { QuizResultOrder } from "@/components/PupilComponents/QuizQuestions/QuizResultOrder";
import { QuizResultMatch } from "@/components/PupilComponents/QuizQuestions/QuizResultMatch";
import { MathJaxWrap } from "@/browser-lib/mathjax/MathJaxWrap";
import { QuestionsArray } from "@/context/PupilLessonQuiz";
import type { QuizQuestion } from "@/node-lib/curriculum-api-2023/queries/pupilLesson/pupilLesson.schema";

type CorectAnswerSectionProps = {
  questionResult: QuestionState;
  quizQuestion: QuestionsArray[number];
};

const CorrectAnswerSection = (props: CorectAnswerSectionProps) => {
  const { questionResult, quizQuestion } = props;

  return (
    <OakFlex $flexDirection={"column"}>
      <OakSpan $font={"body-3-bold"}>
        {Array.isArray(questionResult?.correctAnswer) &&
        questionResult?.correctAnswer.length > 1
          ? "Correct answers:"
          : "Correct answer:"}
      </OakSpan>
      <OakFlex $flexDirection={"column"}>
        {Array.isArray(questionResult?.correctAnswer) &&
          questionResult?.correctAnswer?.map((correctAnswer, index) => {
            const correctAnswerText = isString(correctAnswer)
              ? correctAnswer
              : undefined;

            const imageURL = !isString(correctAnswer)
              ? correctAnswer?.imageObject.secureUrl
              : undefined;

            if (!correctAnswerText && !imageURL) {
              return null;
            }

            return (
              <MathJaxWrap key={`result-${index}`}>
                <OakQuizResultItem
                  key={index}
                  standardText={correctAnswerText}
                  boldPrefixText={pickBoldPretext(
                    quizQuestion?.questionType,
                    index,
                    quizQuestion?.answers,
                  )}
                  imageURL={imageURL}
                  imageAlt={"Image for option " + (index + 1)}
                />
              </MathJaxWrap>
            );
          })}
      </OakFlex>
    </OakFlex>
  );
};

type ResultsInnerProps = {
  index: number;
  displayIndex: number;
  quizArray: QuestionsArray;
  questionResult: QuestionState;
  lessonSection: "exit-quiz" | "starter-quiz";
};

export const QuizResultInner = (props: ResultsInnerProps) => {
  const { index, displayIndex, quizArray, questionResult, lessonSection } =
    props;
  const quizQuestion = quizArray[index];
  const questionStem = quizQuestion?.questionStem;
  const answers = quizQuestion?.answers;
  const grade = questionResult.grade;
  const isHint = questionResult.mode === "init";

  return (
    <OakFlex
      key={`${lessonSection}-question-${index}`}
      $gap={"spacing-16"}
      $flexDirection={["column", "row"]}
      role="listitem"
    >
      <OakIcon
        iconName={grade === 1 ? "tick" : "cross"}
        alt={grade === 1 ? "tick" : "cross"}
        $background={grade === 1 ? "icon-success" : "icon-error"}
        $colorFilter={"text-inverted"}
        $borderRadius={"border-radius-circle"}
        $visibility={isHint ? "hidden" : "visible"}
      />
      <OakFlex $flexDirection={"column"} $gap={"spacing-24"}>
        {questionStem && (
          <QuizResultQuestionStem
            displayIndex={displayIndex}
            questionStem={questionStem}
            isDisabled={questionResult.mode !== "feedback"}
          />
        )}
        {answers?.["multiple-choice"] &&
          Array.isArray(questionResult?.feedback) && (
            <QuizResultMCQ
              answers={answers["multiple-choice"]}
              feedback={questionResult.feedback}
              pupilAnswer={questionResult.pupilAnswer}
            />
          )}
        {answers?.["short-answer"] &&
          !Array.isArray(questionResult?.feedback) &&
          questionResult?.feedback && (
            <QuizResultShortAnswer
              pupilAnswer={questionResult.pupilAnswer}
              feedback={questionResult.feedback}
            />
          )}
        {answers?.["order"] && Array.isArray(questionResult?.feedback) && (
          <QuizResultOrder
            answers={answers["order"]}
            feedback={questionResult.feedback}
            pupilAnswers={questionResult.pupilAnswer}
          />
        )}
        {answers?.["match"] && Array.isArray(questionResult?.feedback) && (
          <QuizResultMatch
            answers={answers["match"]}
            feedback={questionResult.feedback}
            pupilAnswers={questionResult.pupilAnswer}
          />
        )}
        {grade === 0 && quizQuestion && !isHint && (
          <CorrectAnswerSection
            questionResult={questionResult}
            quizQuestion={quizQuestion}
          />
        )}
      </OakFlex>
    </OakFlex>
  );
};

function pickBoldPretext(
  questionType: string | undefined,
  index: number,
  answers: QuizQuestion["answers"],
) {
  switch (true) {
    case questionType === "order":
      return `${index + 1}`;
    case questionType === "match":
      return `${answers?.["match"]?.[index]?.matchOption?.[0]?.text}`;
    default:
      return "";
  }
}
